import { useState, useEffect, useRef } from "react";
import { TimePicker, DateTimePicker, DesktopDatePicker, MobileDatePicker } from "@mui/x-date-pickers";
import { makeStyles } from "@mui/styles";

import colors from "../_colors.scss";

const useStyles = makeStyles(() => ({
	date_picker: {
		color: "black",
		backgroundColor: (props) => colors[props.background] || props.background,
		borderRadius: (props) => props.borderRadius,
		outline: "none",
		width: (props) => props.width,
	},
}));

const DatePicker = ({
	type = "desktop", // desktop, mobile, time, datetime
	value = null,
	onChange,
	onAccept,
	disabled = false,
	label = "",
	views = ["day"],
	background = "third",
	color = "white",
	borderRadius = "10px",
	width = "100%",
	height = "35px",
	required = false,
	autoFocus = false
}) => {
	const classes = useStyles({ background, color, borderRadius, width });
	const [customValue, setCustomValue] = useState(value);
	const inputRef = useRef(null);

	// Update internal state when value prop changes
	useEffect(() => {
		setCustomValue(value);
	}, [value]);

	useEffect(() => {
		if (autoFocus && inputRef.current) {
			requestAnimationFrame(() => {
				setTimeout(() => {
					if (inputRef.current) {
						inputRef.current.focus();
						// Select just the first 2 characters (DD part)
						// This simulates double-clicking on the day section
						inputRef.current.setSelectionRange(0, 2);
					}
				}, 100);
			});
		}
	}, [autoFocus]);

	const handleChange = (newValue) => {
		setCustomValue(newValue);
		if (onChange) onChange(newValue);
	};

	// Common slotProps for the text field
	const getSlotProps = () => ({
		textField: {
			inputRef: inputRef,
			required: required,
			sx: { 
				height, 
				'& .MuiInputBase-input': { 
					height, 
					padding: '0 14px' 
				} 
			}
		}
	});

	return (
		<>
			{type === "desktop" && (
				<DesktopDatePicker
					className={classes.date_picker}
					views={views}
					disabled={disabled}
					label={label}
					format="DD/MM/YYYY"
					value={customValue}
					onChange={handleChange}
					onAccept={onAccept}
					slotProps={getSlotProps()}
				/>
			)}
			{type === "mobile" && (
				<MobileDatePicker
					className={classes.date_picker}
					views={views}
					disabled={disabled}
					label={label}
					format="DD/MM/YYYY"
					value={customValue}
					onChange={handleChange}
					onAccept={onAccept}
					slotProps={getSlotProps()}
				/>
			)}
			{type === "time" && (
				<TimePicker
					className={classes.date_picker}
					disabled={disabled}
					label={label}
					value={customValue}
					onChange={handleChange}
					onAccept={onAccept}
					slotProps={getSlotProps()}
				/>
			)}
			{type === "datetime" && (
				<DateTimePicker
					className={classes.date_picker}
					views={views}
					disabled={disabled}
					label={label}
					format="DD/MM/YYYY HH:mm"
					value={customValue}
					onChange={handleChange}
					onAccept={onAccept}
					slotProps={getSlotProps()}
				/>
			)}
		</>
	);
};

export default DatePicker;
